import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { GridColumn } from '../model/grid-column.model';
import { HttpClient, HttpHeaders, HttpParams, HttpResponse } from '@angular/common/http';
import { HelperService } from './helper.service';
import { GridData } from '../model/grid-data.model';
import { AuthorizationRequest } from '../model/authorization-request.model';
import { AuthorizationResponse } from '../model/authorization-response.model';
import { DivisionsContainer } from '../model/divisions-container.model';
import { Incident } from '../model/incident.model';
import { User } from '../model/user.model';
import { HeaderType } from '../model/header-type.enum';
import { DamageReasonsContainerModel } from '../model/damage-reasons-container.model';
import { DamageTypesContainerModel } from '../model/damage-types-container.model';
import { ReferencedData } from '../model/referenced-data.model';
import { AddressesContainerModel } from '../model/addresses-container.model';
import { LocationsContainerModel } from '../model/locations-container.model';
import { ApplicationInformationItem } from '../model/application-information-item.model';
import { AttachedFile } from '../model/attached-file.model';

@Injectable({
  providedIn: 'root',
})

export class ApiCommunicationService {

  constructor(private http: HttpClient,
              private helperService: HelperService) {
  }

  getDivisions(): Observable<DivisionsContainer> {
    return this.http.get<DivisionsContainer>(`${this.helperService.getWebServiceApiUrl()}/divisions`);
  }

  getGridColumns(headerType: HeaderType): Observable<GridColumn> {
    const urlParameters: HttpParams = new HttpParams().set('header', headerType);
    return this.http.get<GridColumn>(`${this.helperService.getWebServiceApiUrl()}/header`, { params: urlParameters });
  }

  getGridData(headerType: HeaderType): Observable<GridData> {
    const urlParameters: HttpParams = new HttpParams().set('view', headerType);
    return this.http.get<GridData>(`${this.helperService.getWebServiceApiUrl()}/dataGrid`, { params: urlParameters });
  }

  getReferencedData(rowKey: string, parentHeaderType: HeaderType): Observable<ReferencedData> {
    const urlParameters: HttpParams = new HttpParams().set('K3VRowKey', rowKey).set('parentType', parentHeaderType);
    return this.http.get<ReferencedData>(`${this.helperService.getWebServiceApiUrl()}/referencedData`, { params: urlParameters });
  }

  getExistingIncidentOrReport(type: HeaderType, rowKey: string): Observable<Incident> {
    return this.http.get<Incident>(`${this.helperService.getWebServiceApiUrl()}/incident/get/${type}/${rowKey}`);
  }

  getExistingIncidentOrReportBySTMID(type: HeaderType, stmid: string): Observable<Incident> {
    return this.http.get<Incident>(`${this.helperService.getWebServiceApiUrl()}/incident/getBySTMID/${type}/${stmid}`);
  }

  createNewIncidentOrReport(type: HeaderType, module: number): Observable<Incident> {
    return this.http.get<Incident>(`${this.helperService.getWebServiceApiUrl()}/incident/getNew/${type}/${module}`);
  }

  saveIncidentOrReport(model: Partial<Incident>): Observable<Incident> {
    return this.http.post<Incident>(`${this.helperService.getWebServiceApiUrl()}/incident/post`, model);
  }

  cancelIncidentOrReport(stmid: string): Observable<boolean> {
    const urlParameters: HttpParams = new HttpParams().set('STMID', stmid);
    return this.http.get<boolean>(`${this.helperService.getWebServiceApiUrl()}/incident/cancel`, { params: urlParameters });
  }

  createReference(parentRowKey: string, childRowKey: string): Observable<boolean> {
    const urlParameters: HttpParams = new HttpParams().set('ParentRowKey', parentRowKey).set('ChildRowKey', childRowKey);
    return this.http.get<boolean>(`${this.helperService.getWebServiceApiUrl()}/incident/createReference`, { params: urlParameters });
  }

  createReferences(parentRowKey: string, childRowKeys: string[]): Observable<number> {
    const urlParameters: HttpParams = new HttpParams().set('ParentRowKey', parentRowKey).set('ChildRowKeys', childRowKeys.join(', '));
    return this.http.get<number>(`${this.helperService.getWebServiceApiUrl()}/incident/createReferences`, { params: urlParameters });
  }

  getDamageReasons(module: number): Observable<DamageReasonsContainerModel> {
    const urlParameters: HttpParams = new HttpParams().set('module', module);
    return this.http.get<DamageReasonsContainerModel>(`${this.helperService.getWebServiceApiUrl()}/damageReasons`, { params: urlParameters });
  }

  getDamageTypes(module: number): Observable<DamageTypesContainerModel> {
    const urlParameters: HttpParams = new HttpParams().set('module', module);
    return this.http.get<DamageTypesContainerModel>(`${this.helperService.getWebServiceApiUrl()}/damageTypes`, { params: urlParameters });
  }

  login(authorizationDetails: AuthorizationRequest): Observable<AuthorizationResponse> {
    const callHeaders = new HttpHeaders({ 'Content-Type': 'application/x-www-form-urlencoded' });
    const options = { headers: callHeaders };
    const encodedPassword = this.helperService.encodeRFC3986URIComponent(authorizationDetails.password);
    const body = `grant_type=${authorizationDetails.grant_type}&username=${authorizationDetails.username}&password=${encodedPassword}`;
    return this.http.post<AuthorizationResponse>(`${this.helperService.getWebServiceRootUrl()}/authorization`, body, options);
  }

  logout(): Observable<any> {
    return this.http.get<any>(`${this.helperService.getWebServiceApiUrl()}/logoutUser`);
  }

  getUser(username: string): Observable<User> {
    const urlParameters: HttpParams = new HttpParams().set('username', username);
    return this.http.get<User>(`${this.helperService.getWebServiceApiUrl()}/getByUsername`, { params: urlParameters });
  }

  getAddresses(): Observable<AddressesContainerModel> {
    return this.http.get<AddressesContainerModel>(`${this.helperService.getWebServiceApiUrl()}/addresses`);
  }

  getLocations(division: number): Observable<LocationsContainerModel> {
    const urlParameters: HttpParams = new HttpParams().set('Division', division);
    return this.http.get<LocationsContainerModel>(`${this.helperService.getWebServiceApiUrl()}/locations`, { params: urlParameters });
  }

  getApplicationInformation(): Observable<ApplicationInformationItem[]> {
    return this.http.get<ApplicationInformationItem[]>(`${this.helperService.getWebServiceApiUrl()}/info`);
  }

  getAttachedFiles(rowKey: string): Observable<AttachedFile[]> {
    const urlParameters: HttpParams = new HttpParams().set('rowKey', rowKey);
    return this.http.get<AttachedFile[]>(`${this.helperService.getWebServiceApiUrl()}/GetAttachedFiles`, { params: urlParameters });
  }

  downloadFile(k3vID: number): Observable<HttpResponse<ArrayBuffer>> {
    return this.http.get(`${this.helperService.getWebServiceApiUrl()}/downloadFile`, {
      observe: 'response',
      responseType: 'arraybuffer',
      headers: undefined,
      params: { MMID: k3vID }
    });
  }

  uploadFile(rowKey: string, files: FormData): Observable<any> {
    const options: any = { responseType: 'json' };
    options.params = { targetRowKey: rowKey };
    return this.http.post(`${this.helperService.getWebServiceApiUrl()}/UploadFile`, files, options);
  }
}

